// Client for the /api/public/signup/draft/* surface, plus the
// supporting /api/public/{wizard-config,plans,location-key} reads the
// wizard uses to drive its UI.
//
// One-time bearer flow: POST /draft mints a token returned exactly
// once. The SPA stashes that token in sessionStorage (NOT
// localStorage — we want it dropped when the tab closes) and sends
// it as Authorization: Bearer on every follow-up call.
//
// MR-A pivot:
//   * WizardConfig drops promos/emailConfirm/smsConfirm; stages own a
//     required `blocks[]` array of wizardBlockRef.
//   * `fetchWizardConfig` returns the public response shape with
//     `libraryEntries[]` bundled — the SPA resolves every block ref
//     locally.
//   * Draft verification flags moved from top-level
//     `draft.emailVerified` / `draft.smsVerified` to
//     `draft.payload.verified.{email,sms}` so future OtpVerification
//     blocks write through the same channel as every other block.

const TOKEN_STORAGE_KEY = 'bcbb.signup.draft.token';
const DRAFT_ID_STORAGE_KEY = 'bcbb.signup.draft.id';

export interface VerifiedFlags {
  email?: boolean;
  sms?: boolean;
}

export interface DraftPayload {
  intent?: 'ready_to_signup' | 'more_info';
  contact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    smsConsent?: boolean;
  };
  serviceAddress?: DraftAddress;
  mailingAddress?: DraftAddress;
  mailingSameAsService?: boolean;
  locationKey?: string;
  planId?: string;
  quizAnswers?: Record<string, string>;
  upsellAccepted?: Record<string, boolean>;
  billing?: { stripePmId?: string };
  notes?: string;
  utm?: Record<string, string> | null;
  /** Verification flags, populated by OtpVerification blocks (MR-5). */
  verified?: VerifiedFlags;
}

export interface DraftAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postalCode: string;
}

export interface DraftView {
  draftId: string;
  currentStep: string | null;
  /** Server-truth verification flags. Mirrored into `payload.verified`
   *  by the SPA on read so blocks can branch on a single source. */
  emailVerified: boolean;
  smsVerified: boolean;
  completedCustomerId: string | null;
  expiresAt: string;
  payload: DraftPayload;
}

export interface StageDisplayConfig {
  header?: string;
  subheader?: string;
  body?: string;
  testimonials?: {
    mode: 'random' | 'static';
    count: number;
    ids: string[];
  };
}

export interface Testimonial {
  id: string;
  quote: string;
  author: string;
  subtitle?: string;
  stars: number;
}

export interface WizardBlockRef {
  blockId: string;
  libraryId: string;
  libraryVersion: number;
  overrides?: Record<string, unknown>;
  __visibility?: unknown;
}

export interface WizardStage {
  id: string;
  kind: string;
  title?: string;
  blocks: WizardBlockRef[];
  display?: StageDisplayConfig;
}

export interface BundledLibraryEntry {
  id: string;
  version: number;
  archetype: string;
  config: Record<string, unknown>;
}

export interface WizardConfig {
  stages: WizardStage[];
  branches: Array<{ fromStageId: string; when: unknown; goto: string; description?: string }>;
  upsells: Array<{ id: string; triggerWhenPlanIn: string[]; offerPlanId: string; copy: string }>;
  quiz: Array<{ id: string; prompt: string; answers: Array<{ label: string; weights: Record<string, number> }> }>;
  testimonials: Testimonial[];
  /** Bundled library entries — every (libraryId, libraryVersion) ref
   *  in `stages[].blocks[]` resolves to exactly one entry here. */
  libraryEntries: BundledLibraryEntry[];
  version?: number;
}

export interface PlanCatalogEntry {
  id: string;
  name: string;
  monthlyPriceCents: number;
  speedDownMbps: number;
  speedUpMbps: number;
  restricted: boolean;
}

// ── Token storage ──────────────────────────────────────────────────────

export function loadStoredCredentials(): { draftId: string; token: string } | null {
  try {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const draftId = sessionStorage.getItem(DRAFT_ID_STORAGE_KEY);
    if (!token || !draftId) return null;
    return { draftId, token };
  } catch {
    return null;
  }
}

export function storeCredentials(draftId: string, token: string): void {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    sessionStorage.setItem(DRAFT_ID_STORAGE_KEY, draftId);
  } catch {
    /* sessionStorage may be blocked — fall through; the SPA will re-issue a draft on reload */
  }
}

export function clearCredentials(): void {
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(DRAFT_ID_STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

// ── Fetch helpers ──────────────────────────────────────────────────────

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  };
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = (body as { error?: { message?: string } }).error?.message ?? `request failed: ${res.status}`;
    const err = new Error(message);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

/**
 * Mirror server-truth verification flags into `payload.verified` so
 * blocks (and the visibility evaluator) can read from one location.
 * Preserves any payload-side flags written by an OTP block before the
 * server's read completed.
 */
function reshapeDraft(draft: DraftView): DraftView {
  const verified: VerifiedFlags = { ...(draft.payload.verified ?? {}) };
  if (draft.emailVerified) verified.email = true;
  if (draft.smsVerified) verified.sms = true;
  return {
    ...draft,
    payload: { ...draft.payload, verified },
  };
}

// ── Public wizard config ───────────────────────────────────────────────

export async function fetchWizardConfig(): Promise<WizardConfig> {
  const res = await fetch('/api/public/wizard-config?kind=signup', {
    headers: { Accept: 'application/json' },
  });
  return jsonOrThrow<WizardConfig>(res);
}

// ── Public runtime client config ───────────────────────────────────────

export interface SignupClientConfig {
  /** Turnstile site key, or null when the challenge is disabled. */
  turnstileSitekey: string | null;
}

/**
 * Runtime browser config, read from Vault server-side. Kept separate
 * from the wizard config so a Vault/infra value never rides along in
 * the staff-editable, versioned content blob.
 */
export async function fetchSignupConfig(): Promise<SignupClientConfig> {
  const res = await fetch('/api/public/signup/config', {
    headers: { Accept: 'application/json' },
  });
  return jsonOrThrow<SignupClientConfig>(res);
}

// ── Draft lifecycle ────────────────────────────────────────────────────

export async function createDraft(initial: {
  turnstileToken?: string | null;
  payload?: DraftPayload;
}): Promise<{ draftId: string; token: string; config: WizardConfig }> {
  const res = await fetch('/api/public/signup/draft', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      turnstileToken: initial.turnstileToken ?? null,
      payload: initial.payload ?? {},
    }),
  });
  const body = await jsonOrThrow<{ draftId: string; token: string; config: WizardConfig }>(res);
  storeCredentials(body.draftId, body.token);
  return body;
}

export async function getDraft(draftId: string, token: string): Promise<DraftView> {
  const res = await fetch(`/api/public/signup/draft/${draftId}`, {
    headers: authHeaders(token),
  });
  return reshapeDraft(await jsonOrThrow<DraftView>(res));
}

export async function patchDraft(
  draftId: string,
  token: string,
  patch: { payload?: DraftPayload; currentStep?: string },
): Promise<DraftView> {
  const res = await fetch(`/api/public/signup/draft/${draftId}`, {
    method: 'PATCH',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return reshapeDraft(await jsonOrThrow<DraftView>(res));
}

// ── Block-level verification (OtpVerification archetype — MR-5) ──────────

export async function sendVerification(
  draftId: string,
  token: string,
  channel: 'email' | 'sms',
): Promise<void> {
  await jsonOrThrow(
    await fetch('/api/public/signup/verify/send', {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftId, channel }),
    }),
  );
}

export async function confirmVerification(
  draftId: string,
  token: string,
  channel: 'email' | 'sms',
  verifyToken: string,
): Promise<void> {
  await jsonOrThrow(
    await fetch('/api/public/signup/verify/confirm', {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftId, verifyToken }),
    }),
  );
  // `channel` is resolved from the token itself on the server; kept
  // as a param here for future local-state optimistic updates.
  void channel;
}

export async function resumeViaVerification(
  resumeToken: string,
): Promise<{ token: string; draftId: string; channel: 'email' | 'sms' }> {
  const res = await fetch(
    `/api/public/signup/verify/resume?resume=${encodeURIComponent(resumeToken)}`,
    { headers: { Accept: 'application/json' } },
  );
  return jsonOrThrow(res);
}

// ── OTP gates (kept for back-compat until SYS-396 / MR-5 lands) ────────

export async function startEmailVerification(
  draftId: string,
  token: string,
  email?: string,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`/api/public/signup/draft/${draftId}/verify-email/start`, {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(email ? { email } : {}),
    }),
  );
}

export async function confirmEmailVerification(
  draftId: string,
  token: string,
  code: string,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`/api/public/signup/draft/${draftId}/verify-email/confirm`, {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    }),
  );
}

export async function startSmsVerification(
  draftId: string,
  token: string,
  phone?: string,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`/api/public/signup/draft/${draftId}/verify-sms/start`, {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(phone ? { phone } : {}),
    }),
  );
}

export async function confirmSmsVerification(
  draftId: string,
  token: string,
  code: string,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`/api/public/signup/draft/${draftId}/verify-sms/confirm`, {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    }),
  );
}

// ── Stripe setup intent ────────────────────────────────────────────────

export async function createSetupIntent(
  draftId: string,
  token: string,
): Promise<{ clientSecret: string; publishableKey: string; stripeCustomerId: string }> {
  const res = await fetch(`/api/public/signup/draft/${draftId}/setup-intent`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return jsonOrThrow(res);
}

// ── Complete ──────────────────────────────────────────────────────────

export async function completeDraft(
  draftId: string,
  token: string,
): Promise<{
  customerId: string;
  keycloakUserId: string;
  stripeCustomerId: string;
  redirectUrl: string;
}> {
  const res = await fetch(`/api/public/signup/draft/${draftId}/complete`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return jsonOrThrow(res);
}

// ── Plans + location-key ──────────────────────────────────────────────

export async function fetchPlans(
  locationKey: string,
  bearer?: string | null,
): Promise<{ items: PlanCatalogEntry[]; locationKey: string }> {
  const headers: HeadersInit = { Accept: 'application/json' };
  if (bearer) (headers as Record<string, string>).Authorization = `Bearer ${bearer}`;
  const res = await fetch(`/api/public/plans?locationKey=${encodeURIComponent(locationKey)}`, {
    headers,
  });
  return jsonOrThrow(res);
}

export async function resolveLocationKey(
  body: { draftId?: string; line1: string; city?: string; state?: string; postalCode: string; lat?: number | null; lon?: number | null },
  bearer?: string | null,
): Promise<{ locationKey: string; source: 'override' | 'polygon' | 'fallback' }> {
  const headers: HeadersInit = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (bearer) (headers as Record<string, string>).Authorization = `Bearer ${bearer}`;
  const res = await fetch('/api/public/location-key', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}
